/**
 * Sign in with Apple integration. Owns the Apple SDK lifecycle and reports
 * sign-in results to the shared authState facade.
 *
 * Two flows live here:
 *   1. Web: Apple's JS SDK popup (https://appleid.cdn-apple.com/.../appleid.auth.js).
 *      Apple does NOT allow http://localhost as a redirect URI, so the web
 *      flow only works when served from a verified domain.
 *   2. Native iOS (Capacitor): uses @capacitor-community/apple-sign-in,
 *      which calls iOS's AuthenticationServices framework. The user sees a
 *      native sheet (Face ID confirmation) instead of a popup. The plugin is
 *      bundled into the native iOS app and exposed on the page via Capacitor's
 *      bridge — we feature-detect window.Capacitor.Plugins.SignInWithApple so
 *      the remote-loaded web bundle doesn't have to import the npm package.
 */

import { setSignedIn, setSessionToken, setRosterToken, getApiUrl, AuthIdentity } from "./authState";
import { getClientMetadata, withClientMetadata } from "../clientMetadata";

const APPLE_CLIENT_ID = "com.wirdergame.signin";
/**
 * Apple's redirectURI must match exactly one of the Return URLs configured
 * on the Services ID. We use the page's current origin so the same code
 * works on both www.wirdergame.com and beta.wirdergame.com (both are
 * registered). Apple's popup needs to land on the SAME origin as the
 * opener so postMessage back to us isn't blocked cross-origin.
 */
function getRedirectUri(): string {
  return `${window.location.origin}/`;
}

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: { firstName?: string; lastName?: string };
  };
}

interface AppleAuthInitOptions {
  clientId: string;
  scope: string;
  redirectURI: string;
  state?: string;
  usePopup: boolean;
}

interface AppleAuth {
  init(options: AppleAuthInitOptions): void;
  signIn(): Promise<AppleSignInResponse>;
}

/** Subset of @capacitor-community/apple-sign-in we use via the bridge. */
interface CapacitorSignInWithApplePlugin {
  authorize(options: {
    clientId: string;
    redirectURI: string;
    scopes: string;
    state?: string;
    nonce?: string;
  }): Promise<{
    response: {
      user: string;
      identityToken: string;
      authorizationCode?: string;
      email?: string;
      givenName?: string;
      familyName?: string;
    };
  }>;
}

interface CapacitorBridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: {
    SignInWithApple?: CapacitorSignInWithApplePlugin;
    WirderBilling?: import("../storeBilling").NativeBillingPlugin;
    WirderInstallation?: import("../installationChannel").InstallationPlugin;
  };
}

declare global {
  interface Window {
    AppleID?: { auth: AppleAuth };
    Capacitor?: CapacitorBridge;
  }
}

/** True when the page is running inside a Capacitor native iOS WebView. */
export function isNativeIos(): boolean {
  return !!window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === "ios";
}

class AppleAuthClient {
  private _idToken: string | undefined;
  private _initialized = false;
  private _readyPromise: Promise<void> | undefined;

  public init(): void {
    if (this._initialized) return;
    this._initialized = true;
    // Native iOS doesn't need the web SDK to be loaded.
    if (isNativeIos()) {
      this._readyPromise = Promise.resolve();
      return;
    }
    this._readyPromise = this._waitForSdkAndInitialize();
  }

  /** Triggers Apple's sign-in flow (native on iOS, popup on web). */
  public async signIn(): Promise<string> {
    await this._readyPromise;
    if (isNativeIos()) {
      return this._signInNative();
    }
    if (!window.AppleID?.auth) {
      throw new Error("Apple Sign In SDK not loaded");
    }
    const response = await window.AppleID.auth.signIn();
    return this._handleSignIn({
      idToken: response.authorization.id_token,
      email: response.user?.email,
      firstName: response.user?.name?.firstName,
      lastName: response.user?.name?.lastName,
    });
  }

  public async requestFreshToken(): Promise<string> {
    if (this._idToken) return this._idToken;
    return this.signIn();
  }

  public clearCachedToken(): void {
    this._idToken = undefined;
  }

  /* ---------------------- private helpers ---------------------- */

  private async _signInNative(): Promise<string> {
    const plugin = window.Capacitor?.Plugins?.SignInWithApple;
    if (!plugin) {
      throw new Error(
        "SignInWithApple plugin not available. Was the iOS app rebuilt after npm install?",
      );
    }
    // On native iOS, the audience the OS uses for the issued JWT is the App ID
    // (com.wirdergame.app.beta or com.wirdergame.app), NOT the web Services ID.
    // We still pass it because the plugin requires the field; iOS effectively
    // ignores it and uses the bundle identifier. The backend's APPLE_CLIENT_IDS
    // accepts both the Services ID and each App ID.
    const result = await plugin.authorize({
      clientId: APPLE_CLIENT_ID,
      redirectURI: getRedirectUri(),
      scopes: "email name",
    });
    return this._handleSignIn({
      idToken: result.response.identityToken,
      email: result.response.email,
      firstName: result.response.givenName,
      lastName: result.response.familyName,
    });
  }

  private _waitForSdkAndInitialize(): Promise<void> {
    return new Promise<void>((resolve) => {
      const tryInit = () => {
        if (!window.AppleID?.auth) {
          setTimeout(tryInit, 50);
          return;
        }
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: getRedirectUri(),
          usePopup: true,
        });
        resolve();
      };
      tryInit();
    });
  }

  private async _handleSignIn(input: {
    idToken: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  }): Promise<string> {
    const idToken = input.idToken;
    this._idToken = idToken;

    // Apple only returns name on the FIRST sign-in. Capture it now if present.
    const name = [input.firstName, input.lastName].filter(Boolean).join(" ").trim() || undefined;
    const email = input.email;
    const client = await getClientMetadata();

    const meResponse = await fetch(`${getApiUrl()}/me`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(withClientMetadata({ idToken, provider: "apple" }, client)),
    });

    if (!meResponse.ok) {
      const text = await meResponse.text();
      this._idToken = undefined;
      throw new Error(`/api/me failed: HTTP ${meResponse.status} ${text}`);
    }

    const data = (await meResponse.json()) as {
      playerId: string;
      provider: "apple";
      email?: string;
      name?: string;
      sessionToken?: string;
      rosterToken?: string;
    };

    if (data.sessionToken) {
      setSessionToken(data.playerId, data.sessionToken);
    }
    if (data.rosterToken) {
      setRosterToken(data.playerId, data.rosterToken);
    }

    const identity: AuthIdentity = {
      playerId: data.playerId,
      provider: "apple",
      email: email ?? data.email,
      name: name ?? data.name,
    };

    setSignedIn(identity, idToken);
    return idToken;
  }
}

export const appleAuth = new AppleAuthClient();
